import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Users,
  Search,
  Filter,
  ChevronRight,
  MapPin,
  Briefcase,
  Star,
  Clock,
  Eye,
  Video,
  MessageSquare,
  CheckCircle,
  XCircle,
  Award,
  User,
  Calendar,
  Mail,
  FileText,
  ArrowRight,
  RefreshCw,
  CheckSquare,
  Square,
  Loader2
} from "lucide-react";

const ApplicantPipeline = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [applicants, setApplicants] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Selection for bulk actions
  const [selectedApplicants, setSelectedApplicants] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterJob, setFilterJob] = useState(searchParams.get("job") || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "");
  
  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    reviewed: 0,
    shortlisted: 0,
    interviewed: 0,
    offered: 0,
    hired: 0,
    rejected: 0
  });

  useEffect(() => {
    fetchApplicants();
    fetchJobs();
  }, []);

  useEffect(() => {
    // Update URL when filters change
    const params = new URLSearchParams();
    if (filterJob) params.set("job", filterJob);
    if (filterStatus) params.set("status", filterStatus);
    setSearchParams(params);
  }, [filterJob, filterStatus]);

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/candidates/applicants/all`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setApplicants(data.applicants || []);
        
        // Calculate stats
        const newStats = { pending: 0, reviewed: 0, shortlisted: 0, interviewed: 0, offered: 0, hired: 0, rejected: 0 };
        (data.applicants || []).forEach(app => {
          const status = app.application_status || "pending";
          if (newStats[status] !== undefined) newStats[status]++;
        });
        setStats(newStats);
      }
    } catch (error) {
      console.error("Failed to fetch applicants:", error);
      toast.error("Failed to load applicants");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/recruiter`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const handleUpdateStatus = async (applicant, newStatus) => {
    setActionLoading(applicant.user_id);
    try {
      // Find the application_id from the applications collection
      const appResponse = await fetch(`${API}/jobs/${applicant.job_id}/applicants`, {
        credentials: "include",
      });
      
      if (!appResponse.ok) {
        toast.error("Failed to find application");
        return;
      }
      
      const appData = await appResponse.json();
      const application = appData.find(a => a.candidate_id === applicant.user_id);
      
      if (!application) {
        toast.error("Application not found");
        return;
      }

      const response = await fetch(`${API}/jobs/${applicant.job_id}/applications/${application.application_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (response.ok) {
        toast.success(`Applicant status updated to ${newStatus}!`);
        fetchApplicants();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update applicant status");
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk action handlers
  const toggleSelectApplicant = (applicantKey) => {
    setSelectedApplicants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(applicantKey)) {
        newSet.delete(applicantKey);
      } else {
        newSet.add(applicantKey);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedApplicants(new Set());
    } else {
      const allKeys = filteredApplicants.map(a => `${a.user_id}-${a.job_id}`);
      setSelectedApplicants(new Set(allKeys));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkAction = async (newStatus) => {
    if (selectedApplicants.size === 0) {
      toast.error("No applicants selected");
      return;
    }

    setBulkActionLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const key of selectedApplicants) {
      const [userId, jobId] = key.split('-');
      const applicant = applicants.find(a => a.user_id === userId && a.job_id === jobId);
      
      if (!applicant) continue;

      try {
        // Find the application_id
        const appResponse = await fetch(`${API}/jobs/${jobId}/applicants`, {
          credentials: "include",
        });
        
        if (!appResponse.ok) {
          failCount++;
          continue;
        }
        
        const appData = await appResponse.json();
        const application = appData.find(a => a.candidate_id === userId);
        
        if (!application) {
          failCount++;
          continue;
        }

        const response = await fetch(`${API}/jobs/${jobId}/applications/${application.application_id}/status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ status: newStatus }),
        });
        
        if (response.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }

    setBulkActionLoading(false);
    setSelectedApplicants(new Set());
    setSelectAll(false);

    if (successCount > 0) {
      toast.success(`${successCount} applicant(s) ${newStatus} successfully`);
      fetchApplicants();
    }
    if (failCount > 0) {
      toast.error(`Failed to update ${failCount} applicant(s)`);
    }
  };

  const handleSendMessage = (applicantId, applicantName) => {
    navigate(`/messages?to=${applicantId}&name=${encodeURIComponent(applicantName)}`);
  };

  const handleInviteInterview = async (applicantId, applicantName, jobId) => {
    try {
      const response = await fetch(`${API}/ai/interview/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          candidate_id: applicantId,
          job_id: jobId,
          num_questions: 6,
          time_limit_minutes: 30
        }),
      });

      if (response.ok) {
        toast.success(`Interview invitation sent to ${applicantName}!`);
        fetchApplicants();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send interview invitation");
    }
  };

  // Filter applicants
  const filteredApplicants = applicants.filter(applicant => {
    const matchesSearch = !searchQuery || 
      applicant.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.headline?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      applicant.skills?.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesJob = !filterJob || applicant.job_id === filterJob;
    const matchesStatus = !filterStatus || applicant.application_status === filterStatus;
    
    return matchesSearch && matchesJob && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      reviewed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      shortlisted: "bg-green-500/20 text-green-400 border-green-500/30",
      interviewed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      offered: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      hired: "bg-green-600/20 text-green-500 border-green-600/30",
      rejected: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return colors[status] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const statusOptions = [
    { value: "", label: "All Statuses", count: applicants.length },
    { value: "pending", label: "Pending Review", count: stats.pending, icon: Clock },
    { value: "reviewed", label: "Reviewed", count: stats.reviewed, icon: Eye },
    { value: "shortlisted", label: "Shortlisted", count: stats.shortlisted, icon: Star },
    { value: "interviewed", label: "Interviewed", count: stats.interviewed, icon: Video },
    { value: "offered", label: "Offered", count: stats.offered, icon: Award },
    { value: "hired", label: "Hired", count: stats.hired, icon: CheckCircle },
    { value: "rejected", label: "Rejected", count: stats.rejected, icon: XCircle },
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />

      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold">
                APPLICANT <span className="text-crimson">PIPELINE</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Manage and track all your job applicants</p>
            </div>
            <Button
              onClick={fetchApplicants}
              variant="outline"
              className="border-[#333] text-white hover:border-crimson text-sm sm:text-base"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Pipeline Stats Bar */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-3 sm:p-4 mb-6 overflow-x-auto -mx-4 sm:mx-0"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-max px-4 sm:px-0">
              {statusOptions.map((status, index) => (
                <button
                  key={status.value}
                  onClick={() => setFilterStatus(status.value)}
                  className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border transition-all text-xs sm:text-sm ${
                    filterStatus === status.value 
                      ? "border-crimson bg-crimson/10 text-crimson" 
                      : "border-[#333] hover:border-[#444] text-gray-400 hover:text-white"
                  }`}
                  data-testid={`filter-status-${status.value || 'all'}`}
                >
                  {status.icon && <status.icon className="w-3 h-3 sm:w-4 sm:h-4" />}
                  <span className="font-medium">{status.count}</span>
                  <span className="hidden sm:inline">{status.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Filters */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-4 mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, headline, or skills..."
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 pl-10"
                  data-testid="search-input"
                />
              </div>
              
              {/* Job Filter */}
              <select
                value={filterJob}
                onChange={(e) => setFilterJob(e.target.value)}
                className="px-4 py-2 bg-obsidian border border-[#333] text-white min-w-[200px]"
                data-testid="filter-job"
              >
                <option value="">All Jobs</option>
                {jobs.map(job => (
                  <option key={job.job_id} value={job.job_id}>{job.title}</option>
                ))}
              </select>
              
              {/* Clear Filters */}
              {(filterJob || filterStatus || searchQuery) && (
                <Button
                  onClick={() => {
                    setFilterJob("");
                    setFilterStatus("");
                    setSearchQuery("");
                  }}
                  variant="outline"
                  className="border-[#333] text-gray-400 hover:text-white"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </motion.div>

          {/* Results Summary */}
          <div className="mb-4 flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Showing {filteredApplicants.length} of {applicants.length} applicants
              {filterStatus && ` with status "${filterStatus}"`}
              {filterJob && ` for "${jobs.find(j => j.job_id === filterJob)?.title}"`}
            </p>
          </div>

          {/* Bulk Actions Bar */}
          {filteredApplicants.length > 0 && (
            <motion.div
              className="mb-4 bg-obsidian-light border border-[#222] p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    data-testid="select-all-btn"
                  >
                    {selectAll ? (
                      <CheckSquare className="w-5 h-5 text-crimson" />
                    ) : (
                      <Square className="w-5 h-5" />
                    )}
                    <span className="text-sm">
                      {selectAll ? "Deselect All" : "Select All"}
                    </span>
                  </button>
                  
                  {selectedApplicants.size > 0 && (
                    <span className="text-crimson text-sm font-medium">
                      {selectedApplicants.size} selected
                    </span>
                  )}
                </div>

                <AnimatePresence>
                  {selectedApplicants.size > 0 && (
                    <motion.div
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <span className="text-gray-500 text-sm">Bulk Actions:</span>
                      
                      <Button
                        onClick={() => handleBulkAction("reviewed")}
                        disabled={bulkActionLoading}
                        variant="outline"
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        data-testid="bulk-review-btn"
                      >
                        {bulkActionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Eye className="w-4 h-4 mr-2" />
                        )}
                        Mark Reviewed
                      </Button>
                      
                      <Button
                        onClick={() => handleBulkAction("shortlisted")}
                        disabled={bulkActionLoading}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="bulk-shortlist-btn"
                      >
                        {bulkActionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Star className="w-4 h-4 mr-2" />
                        )}
                        Shortlist All
                      </Button>
                      
                      <Button
                        onClick={() => handleBulkAction("rejected")}
                        disabled={bulkActionLoading}
                        variant="outline"
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        data-testid="bulk-reject-btn"
                      >
                        {bulkActionLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <XCircle className="w-4 h-4 mr-2" />
                        )}
                        Reject All
                      </Button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredApplicants.length === 0 ? (
            <motion.div
              className="bg-obsidian-light border border-[#222] p-12 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl font-bold mb-2">No Applicants Found</h3>
              <p className="text-gray-400 mb-6">
                {searchQuery || filterJob || filterStatus
                  ? "Try adjusting your filters to see more candidates"
                  : "Post jobs to receive applications from candidates"}
              </p>
              {!searchQuery && !filterJob && !filterStatus && (
                <Button
                  onClick={() => navigate("/jobs/create")}
                  className="bg-crimson hover:bg-crimson/90"
                >
                  Post a Job
                </Button>
              )}
            </motion.div>
          ) : (
            <div className="space-y-4">
              {filteredApplicants.map((applicant, i) => {
                const applicantKey = `${applicant.user_id}-${applicant.job_id}`;
                const isSelected = selectedApplicants.has(applicantKey);
                
                return (
                <motion.div
                  key={applicantKey}
                  className={`bg-obsidian-light border p-6 hover:border-[#333] transition-colors ${
                    isSelected ? "border-crimson/50 bg-crimson/5" : "border-[#222]"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  data-testid={`applicant-${applicant.user_id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Candidate Info */}
                    <div className="flex items-start gap-4 flex-1">
                      {/* Selection Checkbox */}
                      <button
                        onClick={() => toggleSelectApplicant(applicantKey)}
                        className="mt-1 flex-shrink-0"
                        data-testid={`select-${applicant.user_id}`}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-crimson" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-500 hover:text-gray-300" />
                        )}
                      </button>
                      
                      <div className="w-14 h-14 bg-crimson/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {applicant.picture ? (
                          <img src={applicant.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-crimson" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-bold text-white">
                          {applicant.name || "Anonymous"}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2">
                          {applicant.headline || "Job Seeker"}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-2">
                          {applicant.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" /> {applicant.location}
                            </span>
                          )}
                          {applicant.experience_years > 0 && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" /> {applicant.experience_years} years exp
                            </span>
                          )}
                          {applicant.match_score && (
                            <span className={`flex items-center gap-1 ${
                              applicant.match_score >= 70 ? "text-green-400" :
                              applicant.match_score >= 40 ? "text-yellow-400" : "text-gray-400"
                            }`}>
                              <Star className="w-4 h-4" /> {applicant.match_score}% match
                            </span>
                          )}
                        </div>
                        
                        {/* Skills */}
                        {applicant.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {applicant.skills.slice(0, 5).map((skill) => (
                              <span key={skill} className="px-2 py-0.5 bg-obsidian border border-[#333] text-xs text-gray-300">
                                {skill}
                              </span>
                            ))}
                            {applicant.skills.length > 5 && (
                              <span className="px-2 py-0.5 text-xs text-gray-500">
                                +{applicant.skills.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Job & Status */}
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <span className="text-xs text-gray-500 block mb-1">Applied for:</span>
                        <button 
                          onClick={() => navigate(`/jobs/${applicant.job_id}`)}
                          className="text-crimson hover:underline text-sm font-medium"
                        >
                          {applicant.job_title}
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 text-xs uppercase font-medium border ${getStatusColor(applicant.application_status || "pending")}`}>
                          {applicant.application_status || "Pending"}
                        </span>
                        {applicant.has_interview && (
                          <span className={`px-2 py-1 text-xs flex items-center gap-1 ${
                            applicant.interview_status === "completed" ? "bg-green-500/20 text-green-400" :
                            "bg-blue-500/20 text-blue-400"
                          }`}>
                            <Video className="w-3 h-3" />
                            {applicant.interview_status === "completed" ? "Done" : "Invited"}
                          </span>
                        )}
                      </div>

                      {applicant.applied_at && (
                        <span className="text-xs text-gray-500">
                          {new Date(applicant.applied_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-2 pt-4 border-t border-[#333]">
                    <Button
                      onClick={() => navigate(`/candidates/${applicant.user_id}`)}
                      variant="outline"
                      size="sm"
                      className="border-[#333] text-white hover:bg-white/5"
                      data-testid={`view-profile-${applicant.user_id}`}
                    >
                      <FileText className="w-4 h-4 mr-2" /> View Profile
                    </Button>
                    <Button
                      onClick={() => handleSendMessage(applicant.user_id, applicant.name)}
                      variant="outline"
                      size="sm"
                      className="border-[#333] text-white hover:bg-white/5"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" /> Message
                    </Button>
                    
                    {/* Status Actions */}
                    {applicant.application_status === "pending" && (
                      <>
                        <Button
                          onClick={() => handleUpdateStatus(applicant, "reviewed")}
                          disabled={actionLoading === applicant.user_id}
                          variant="outline"
                          size="sm"
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Eye className="w-4 h-4 mr-2" /> Mark Reviewed
                        </Button>
                        <Button
                          onClick={() => handleUpdateStatus(applicant, "shortlisted")}
                          disabled={actionLoading === applicant.user_id}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Star className="w-4 h-4 mr-2" /> Shortlist
                        </Button>
                      </>
                    )}
                    
                    {(applicant.application_status === "reviewed" || applicant.application_status === "shortlisted") && (
                      <>
                        {!applicant.has_interview && (
                          <Button
                            onClick={() => handleInviteInterview(applicant.user_id, applicant.name, applicant.job_id)}
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <Video className="w-4 h-4 mr-2" /> Invite Interview
                          </Button>
                        )}
                        {applicant.application_status !== "shortlisted" && (
                          <Button
                            onClick={() => handleUpdateStatus(applicant, "shortlisted")}
                            disabled={actionLoading === applicant.user_id}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Star className="w-4 h-4 mr-2" /> Shortlist
                          </Button>
                        )}
                      </>
                    )}
                    
                    {applicant.application_status === "interviewed" && (
                      <>
                        <Button
                          onClick={() => handleUpdateStatus(applicant, "offered")}
                          disabled={actionLoading === applicant.user_id}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Award className="w-4 h-4 mr-2" /> Make Offer
                        </Button>
                      </>
                    )}
                    
                    {applicant.application_status === "offered" && (
                      <Button
                        onClick={() => handleUpdateStatus(applicant, "hired")}
                        disabled={actionLoading === applicant.user_id}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Mark Hired
                      </Button>
                    )}
                    
                    {applicant.has_interview && applicant.interview_status === "completed" && (
                      <Button
                        onClick={() => navigate(`/interviews/${applicant.interview_session_id}`)}
                        variant="outline"
                        size="sm"
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> View Interview
                      </Button>
                    )}
                    
                    {applicant.application_status !== "rejected" && applicant.application_status !== "hired" && (
                      <Button
                        onClick={() => handleUpdateStatus(applicant, "rejected")}
                        disabled={actionLoading === applicant.user_id}
                        variant="outline"
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ApplicantPipeline;
